import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import Layout from '../../components/Layout'
import { sortPeople } from '../../utils/peopleSort'

interface ProjectSummary {
  id: number
  name: string
  description: string | null
  status: string
  health: string
  progress_percent: number
  owner: { id: number; full_name: string; avatar_text: string | null }
  linked_rock: { id: number; title: string } | null
  latest_status_update_summary: string | null
  latest_status_update_at: string | null
  member_count: number
  archived: boolean
  updated_at: string
}

interface Props {
  projects: ProjectSummary[]
  teams: { id: number; name: string }[]
  org_members: { id: number; full_name: string }[]
  archived: boolean
  filters: { q?: string; owner_id?: string; team_id?: string }
  can_create: boolean
  templates: { id: number; name: string; description: string | null }[]
}

const HEALTH_COLORS: Record<string, string> = {
  on_track: 'bg-green-100 text-green-800',
  at_risk: 'bg-amber-100 text-amber-800',
  off_track: 'bg-red-100 text-red-800'
}

function relativeTime(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function ProjectsIndex({ projects, teams, org_members, archived, filters, can_create, templates }: Props) {
  const [q, setQ] = useState(filters.q || '')
  const [ownerId, setOwnerId] = useState(filters.owner_id || '')
  const [teamId, setTeamId] = useState(filters.team_id || '')
  const [view, setView] = useState<'list' | 'cards' | 'board'>(() => (typeof window !== 'undefined' && (localStorage.getItem('projectsView') as any)) || 'list')

  function setAndPersistView(v: 'list' | 'cards' | 'board') {
    setView(v)
    if (typeof window !== 'undefined') localStorage.setItem('projectsView', v)
  }

  function applyFilters(updates: Partial<{ q: string; owner_id: string; team_id: string }> = {}) {
    const next = { q, owner_id: ownerId, team_id: teamId, ...updates }
    router.get('/projects', next, { preserveState: true, preserveScroll: true })
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{ color: '#1B1B1B' }}>Projects</h1>
        <div className="flex items-center gap-3">
          <Link
            href={archived ? '/projects' : '/projects/archived'}
            className="px-4 py-2 text-sm border rounded-lg"
          >
            {archived ? 'Active' : 'Archived'}
          </Link>
          <Link href="/project_templates" className="px-4 py-2 text-sm border rounded-lg">Templates</Link>
          {can_create && (
            <Link
              href="/projects/new"
              className="px-5 py-2.5 text-base font-medium text-white rounded-lg"
              style={{ backgroundColor: '#ED1C24' }}
            >
              + New Project
            </Link>
          )}
        </div>
      </div>

      {!archived && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-text-muted mb-1">Search</label>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters() }}
              placeholder="Search project name..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Owner</label>
            <select value={ownerId} onChange={(e) => { setOwnerId(e.target.value); applyFilters({ owner_id: e.target.value }) }} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All owners</option>
              {sortPeople(org_members).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Team space</label>
            <select value={teamId} onChange={(e) => { setTeamId(e.target.value); applyFilters({ team_id: e.target.value }) }} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All teams</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <button onClick={() => applyFilters()} className="px-4 py-2 text-sm bg-[#351E1D] text-white rounded-lg">Apply</button>
          <div className="ml-auto flex items-center gap-1 border rounded-lg p-1 text-xs">
            {(['list', 'cards', 'board'] as const).map((v) => (
              <button key={v} onClick={() => setAndPersistView(v)} className={`px-2 py-1 rounded ${view === v ? 'bg-[#351E1D] text-white' : 'text-text-secondary'}`}>
                {v === 'board' ? 'Board (by health)' : v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-text-secondary font-medium">No projects yet</p>
          {!archived && templates.length > 0 && (
            <p className="text-sm text-text-muted mt-1">Try starting from a template above.</p>
          )}
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      ) : view === 'board' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(['on_track', 'at_risk', 'off_track'] as const).map((h) => (
            <div key={h} className="bg-gray-50 border rounded-lg p-3">
              <h2 className="text-xs uppercase font-semibold text-text-muted mb-2">{h.replace('_', ' ')}</h2>
              <div className="space-y-2">
                {projects.filter((p) => p.health === h).map((p) => <ProjectCard key={p.id} p={p} />)}
                {projects.filter((p) => p.health === h).length === 0 && <p className="text-xs text-text-muted">—</p>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-text-muted uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Health</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Last update</th>
                <th className="px-4 py-3">Members</th>
                <th className="px-4 py-3">Rock</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link href={`/projects/${p.id}`} className="font-medium text-text-primary hover:underline">
                      {p.name}
                    </Link>
                    {p.description && <div className="text-xs text-text-muted truncate max-w-md">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{p.owner?.full_name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${HEALTH_COLORS[p.health] || ''}`}>
                      {p.health.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 w-40">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-[#15532E]" style={{ width: `${p.progress_percent}%` }} />
                      </div>
                      <span className="text-xs text-text-secondary w-10 text-right">{p.progress_percent}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-muted">{relativeTime(p.latest_status_update_at)}</td>
                  <td className="px-4 py-3 text-text-secondary">{p.member_count}</td>
                  <td className="px-4 py-3 text-xs text-text-muted">{p.linked_rock?.title || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  )
}

function ProjectCard({ p }: { p: ProjectSummary }) {
  return (
    <Link href={`/projects/${p.id}`} className="block bg-white border rounded-lg p-3 hover:border-[#351E1D]">
      <div className="flex items-center justify-between">
        <span className="font-medium text-text-primary">{p.name}</span>
        <span className={`text-xs px-2 py-0.5 rounded ${HEALTH_COLORS[p.health] || ''}`}>{p.health.replace('_', ' ')}</span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
          <div className="h-full bg-[#15532E]" style={{ width: `${p.progress_percent}%` }} />
        </div>
        <span className="text-xs text-text-secondary w-10 text-right">{p.progress_percent}%</span>
      </div>
      <div className="text-xs text-text-muted mt-2">{p.owner?.full_name} · {p.member_count} members</div>
    </Link>
  )
}
